import type { BrandKey } from '../config/brands';

export interface BrandNavigationVisibility {
  showCreateCitrusTreeArticles: boolean;
  showRewriteBlogPosts: boolean;
  showPeopleAlsoAskAgent: boolean;
  showNewTopicsAgent: boolean;
  showBestOfArticlesAgent: boolean;
  showCitrusQuiz: boolean;
  showCitrusDoctor: boolean;
}

// Feature flags configuration - centralized control for feature visibility
export interface FeatureFlags {
  // Master toggle: When false, hides everything except Blog Agents, Kits, and Knowledge Base
  showAllNavItems: boolean;
  showCRO: boolean;
  showGongAnalysis: boolean;
  showLandingPageAnalyzer: boolean;
  showBlogCreator: boolean;
  showPlaybooksCreator: boolean;
  showBrandKit: boolean;
  showVoCKit: boolean; // Controls visibility of VoC Kit in Kits section
  showCompetitorConquesting: boolean; // Controls visibility of "Outrank Competitors" in Blog Agents
  showBlogHistory: boolean; // Controls visibility of "History" in Blog Agents
  showGongFullConversationTab: boolean;
  showPreviewArticle: boolean;
  showConcurrentExecution: boolean; // Controls visibility of "Run 5 Rows Concurrently" feature
  showAllSubreddits: boolean; // Controls visibility of "All Subreddits" option in dropdown
  showIndividualPostsView: boolean; // Controls visibility of "View Individual Posts" button
  showKitsSection: boolean; // Controls visibility of entire Kits section in navigation
  showKnowledgeBaseSection: boolean; // Controls visibility of entire Knowledge Base section in navigation
  brandAvailability: Record<BrandKey, boolean>; // Controls whether each brand is navigable in the brand switcher/routes
  brandNavigationVisibility: Record<BrandKey, BrandNavigationVisibility>; // Controls brand-specific nav item visibility
}

export const FEATURE_FLAGS: FeatureFlags = {
  // MASTER TOGGLE: Set to true to show all navigation items, false to show only Blog Agents, Kits, and Knowledge Base
  showAllNavItems: false,
  showCRO: false, // Hide CRO for production
  showGongAnalysis: false,
  showLandingPageAnalyzer: false, // Hide Landing Page Analyzer from navigation
  showBlogCreator: true,
  showPlaybooksCreator: true,
  showBrandKit: true,
  showVoCKit: true, // Set to true to show VoC Kit in Kits section
  showCompetitorConquesting: false, // Set to true to show "Outrank Competitors" in Blog Agents
  showBlogHistory: false, // Set to true to show "History" in Blog Agents
  showGongFullConversationTab: false, // Hide until transcript data is available
  showPreviewArticle: false, // Hide Preview Article button functionality
  showConcurrentExecution: false, // Hide concurrent execution feature for now
  showAllSubreddits: false, // Set to false to hide "All Subreddits" option
  showIndividualPostsView: false, // Set to false to hide "View Individual Posts" button
  showKitsSection: false, // Set to true to show Kits section in navigation
  showKnowledgeBaseSection: false, // Set to true to show Knowledge Base section in navigation
  brandAvailability: {
    uscitrusnursery: true,
    drmanismagic: true,
    uscitrus: false, // Flip to true when the US Citrus brand is ready to launch
    shoproyalgold: false, // Flip to true when the Royal Gold brand is ready to launch
  },
  brandNavigationVisibility: {
    uscitrusnursery: {
      showCreateCitrusTreeArticles: true,
      showRewriteBlogPosts: true,
      showPeopleAlsoAskAgent: true,
      showNewTopicsAgent: true,
      showBestOfArticlesAgent: true,
      showCitrusQuiz: true,
      showCitrusDoctor: true,
    },
    drmanismagic: {
      showCreateCitrusTreeArticles: false,
      showRewriteBlogPosts: false,
      showPeopleAlsoAskAgent: false,
      showNewTopicsAgent: false,
      showBestOfArticlesAgent: true,
      showCitrusQuiz: true,
      showCitrusDoctor: false,
    },
    uscitrus: {
      showCreateCitrusTreeArticles: true,
      showRewriteBlogPosts: true,
      showPeopleAlsoAskAgent: true,
      showNewTopicsAgent: true,
      showBestOfArticlesAgent: false,
      showCitrusQuiz: true,
      showCitrusDoctor: true,
    },
    shoproyalgold: {
      showCreateCitrusTreeArticles: true,
      showRewriteBlogPosts: true,
      showPeopleAlsoAskAgent: true,
      showNewTopicsAgent: true,
      showBestOfArticlesAgent: false,
      showCitrusQuiz: true,
      showCitrusDoctor: true,
    },
  },
}; 